import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IconGraphy, Table, Tooltip } from '@leapeasy/ui-kit';
import { Dialog } from '@mui/material';
import styled from 'styled-components';
import { CancelDialog } from 'components/common/Dialog/CancelDialog';
import IMG_NOFI_1 from 'assets/bill-1.png';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import {
  EmailTextCell,
  InviteTypeBadge,
  RequestStatusBadge,
  RoleBadge,
} from './notificationTableRenderers';
import { GlobalUXState } from 'components/GlobalUXState';
import { useDemoMask } from 'hooks/useDemoMask';
import { buildMaskedOutgoingNotificationRow } from 'utils/demoMask';

export const NotificationToTable = forwardRef(
  (
    {
      messages,
      filter,
      dropFilter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onClickMessage,
      onClickDelete,
      onClickResend,
      onClickRow,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
    },
    ref,
  ) => {
    const { isDemo } = useDemoMask();
    const [tableData, setTableData] = useState([]);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));

    useEffect(() => {
      if (!messages) return;
      const sorted = [...messages].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      setTableData(sorted.map((message) => buildMaskedOutgoingNotificationRow(message, isDemo)));
    }, [messages, isDemo]);

    const findMessage = (tableMeta) => {
      const id = tableMeta[tableMeta.length - 2];
      return messages.find((message) => message.id === id);
    };

    const goDetailPage = (tableMeta) => {
      const selectedMessage = findMessage(tableMeta);
      if (selectedMessage) onClickMessage(selectedMessage);
    };

    const [deleteId, setDeletedId] = useState(null);
    const handleDelButton = () => {
      onClickDelete(deleteId);
      setDeletedId(null);
    };

    return (
      <React.Fragment>
        <Dialog open={!!deleteId}>
          <CancelDialog
            img={IMG_NOFI_1}
            title="Are you sure you want to delete this notification?"
            detail=""
            noFunc={() => setDeletedId(null)}
            yesFunc={handleDelButton}
          />
        </Dialog>
        <Table
          showEmptyState
          emptyContent={
            <GlobalUXState
              variant={filter?.searchText ? 'noResults' : 'noData'}
              compact
              description={
                filter?.searchText
                  ? 'No outgoing notifications match your search.'
                  : 'No outgoing notifications available yet.'
              }
            />
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          onRequestExportData={
            onRequestExportData
              ? async () => {
                  const rows = await onRequestExportData();
                  return (rows || []).map((message) =>
                    buildMaskedOutgoingNotificationRow(message, isDemo),
                  );
                }
              : undefined
          }
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const rows = await onRequestFullExportData();
                  return (rows || []).map((message) =>
                    buildMaskedOutgoingNotificationRow(message, isDemo),
                  );
                }
              : undefined
          }
          data={tableData}
          autoTooltipForDefaultCells
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          onClickRow={
            onClickRow
              ? (row) => {
                  const message = row?.[row.length - 1];
                  if (message?.id) onClickRow(message);
                }
              : undefined
          }
          style={{ width: '100%' }}
          title="Outgoing notifications"
          columns={[
            {
              name: 'Invite Type',
              key: 'request_type',
              options: {
                flex: '40px 1 1',
                sort: true,
                customBodyRenderer: (value) => <InviteTypeBadge value={value} />,
              },
            },
            {
              name: 'Status',
              key: 'request_status',
              options: {
                flex: '40px 1 1',
                sort: true,
                customBodyRenderer: (value) => <RequestStatusBadge value={value} />,
              },
            },
            {
              name: 'To Email',
              key: 'accepter_email',
              options: { sort: true },
            },
            {
              name: 'To Role',
              key: 'accepter_role',
              options: {
                flex: '40px 1 1',
                sort: true,
                customBodyRenderer: (value) => <RoleBadge role={value} textSize="small" />,
              },
            },
            {
              name: 'From Email',
              key: 'requestor_email',
              options: { sort: true },
            },
            {
              name: 'From Role',
              key: 'requestor_role',
              options: {
                flex: '40px 1 1',
                sort: true,
                customBodyRenderer: (value) => <RoleBadge role={value} />,
              },
            },
            {
              name: 'Text',
              key: 'email_text',
              options: {
                flex: '120px 1 1',
                sort: false,
                customBodyRenderer: (value) => <EmailTextCell html={value} />,
              },
            },
            {
              name: 'Property Count',
              key: 'property.length',
              options: { sort: true },
            },
            {
              name: 'Date',
              key: 'updatedAt',
              options: { sort: true },
            },
            {
              name: 'Detail',
              key: 'detail',
              options: {
                sort: false,
                flex: '5px 1 1',
                customBodyRenderer: (_value, tableMeta) => (
                  <Tooltip title="View notification details" placement="top" showCarot>
                    <div>
                      <IconGraphy
                        icon="FileFolder.Description"
                        style={{ color: '#702572', margin: 'auto', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          goDetailPage(tableMeta);
                        }}
                      />
                    </div>
                  </Tooltip>
                ),
              },
            },
            {
              name: '',
              key: 'actions',
              options: {
                sort: false,
                flex: '80px 0 0',
                customBodyRenderer: (_value, tableMeta) => {
                  const selectedMessage = findMessage(tableMeta);
                  if (!selectedMessage) return null;
                  const isExpired = selectedMessage.request_status === 'expired';
                  return (
                    <ActionRow>
                      {isExpired ? (
                        <Tooltip title="Resend invitation" placement="top" showCarot>
                          <div>
                            <IconGraphy
                              icon="EditorLayout.Send"
                              style={{ color: '#702572', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onClickResend?.(selectedMessage);
                              }}
                            />
                          </div>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Delete notification" placement="top" showCarot>
                        <div>
                          <IconGraphy
                            icon="General.Delete"
                            style={{ color: '#702572', cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletedId(selectedMessage.id);
                            }}
                          />
                        </div>
                      </Tooltip>
                    </ActionRow>
                  );
                },
              },
            },
          ]}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Outgoing notifications"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </React.Fragment>
    );
  },
);

NotificationToTable.displayName = 'NotificationToTable';

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
`;
